import {
  ContentfulClientApi,
  createClient,
  Entry,
  EntriesQueries,
  EntryCollection
} from 'contentful';
import {
  CCPOfferingContent,
  CCPOfferingContentSkeleton,
  CCPPricingPlanContent,
  CCPPricingPlanItem,
  CCPPricingPlanTier,
  CCPProductContentSkeleton
} from './ccp-product-catalog-client.types';
import { contentTypeIds, DEFAULT_HOST } from './constants';
import { CCPAddonProduct, OfferingSKU, OfferingSKUMap } from './models';

export type CCPProductCatalogClientOptions = {
  readonly space: string;
  readonly environment: string;
  readonly accessToken: string;
  readonly host?: string;
};

// the Contentful SDK does not actually include these methods in the Entry<T> object
export type ContentfulOffering = Omit<
  Entry<CCPOfferingContentSkeleton>,
  'toPlainObject' | 'update'
>;

export interface ICCPProductCatalogClient {
  getOfferingsBySKU({
    offeringSKUs
  }: GetCCPPricingOptions): Promise<OfferingSKUMap<CCPOfferingContent>>;

  ccpSearchAddons({
    query,
    limit
  }: CCPSearchAddonsOptions): Promise<CCPAddonProduct[]>;

  getOfferingsByProductKey(
    key: string
  ): Promise<EntryCollection<CCPOfferingContentSkeleton>>;

  getOfferingSkuByProductKey(key: string): Promise<string>;
}

export class CCPProductCatalogClient implements ICCPProductCatalogClient {
  private readonly contentfulClient: ContentfulClientApi<undefined>;

  public constructor(options: CCPProductCatalogClientOptions) {
    this.contentfulClient = createClient({
      ...options,
      host: options.host || DEFAULT_HOST
    });
  }

  public async getOfferingsBySKU({
    offeringSKUs
  }: GetCCPPricingOptions): Promise<OfferingSKUMap<CCPOfferingContent>> {
    const { items } =
      await this.contentfulClient.getEntries<CCPOfferingContentSkeleton>({
        'content_type': contentTypeIds.ccpOffering,
        //@ts-ignore
        'fields.sku[in]': [...new Set(offeringSKUs).values()]
      });

    let ret: OfferingSKUMap<CCPOfferingContent> = {};
    for (const offeringSKU of offeringSKUs) {
      const entry = items.find(({ fields: { sku } }) => sku === offeringSKU);
      const contentfulOffering = entry as unknown as CCPOfferingContent;

      ret = {
        ...ret,
        [offeringSKU]: contentfulOffering || undefined
      };
    }

    return ret;
  }

  /* istanbul ignore next */
  public async ccpSearchAddons({
    query,
    limit
  }: CCPSearchAddonsOptions): Promise<CCPAddonProduct[]> {
    const { items } =
      await this.contentfulClient.getEntries<CCPProductContentSkeleton>({
        'content_type': contentTypeIds.ccpProduct,
        'select': ['fields.key', 'fields.name'],
        //@ts-ignore
        'fields.name[match]': query,
        limit
      });

    const addonProducts: CCPAddonProduct[] = items.map((item) => {
      const {
        fields: { name, key }
      } = item;

      return {
        name: name ?? '',
        ccpProductKey: key
      };
    });

    return addonProducts;
  }

  public async getOfferingsByProductKey(key: string) {
    return this.contentfulClient.getEntries<CCPOfferingContentSkeleton>({
      'content_type': contentTypeIds.ccpOffering,
      'fields.productKey': key
    } as EntriesQueries<CCPOfferingContentSkeleton, undefined>);
  }

  /* istanbul ignore next */
  public async getOfferingSkuByProductKey(key: string): Promise<string> {
    const { items: offerings } =
      await this.contentfulClient.getEntries<CCPOfferingContentSkeleton>({
        'content_type': contentTypeIds.ccpOffering,
        //@ts-ignore
        'fields.productKey': key
      });

    return offerings[0]?.fields.sku ?? '';
  }
}

export type GetCCPPricingOptions = {
  readonly offeringSKUs: ReadonlyArray<OfferingSKU>;
};

export type CCPSearchAddonsOptions = {
  readonly query: string;
  readonly limit?: number;
};

export { CCPPricingPlanContent, CCPPricingPlanItem, CCPPricingPlanTier };
